/**
 * Pure terrain/geometry kernel shared by the simulator and a future game loop.
 * No RNG, rendering, policy, or mutable combat state belongs in this module.
 */

export interface Vec2 {
  x: number;
  y: number;
}

export type Shape =
  | { type: 'circle'; center: Vec2; radiusM: number }
  | { type: 'rect'; minX: number; minY: number; maxX: number; maxY: number };

export type TerrainMaterial = 'wall' | 'pillar' | 'breakable';

export interface Obstacle {
  id: string;
  shape: Shape;
  material: TerrainMaterial;
  blocksMovement: boolean;
  blocksProjectile: boolean;
  blocksAreaEffect: boolean;
}

export type ZoneKind = 'hazard' | 'safe' | 'slow' | 'cliff';

export interface TerrainZone {
  id: string;
  shape: Shape;
  kind: ZoneKind;
  /** HP/s for hazard, damage-reduction fraction for safe, slow fraction for slow. */
  intensity: number;
}

export interface Terrain {
  boundary: { type: 'circle'; center: Vec2; radiusM: number };
  obstacles: Obstacle[];
  zones: TerrainZone[];
}

export interface TerrainHit {
  t: number;
  point: Vec2;
  normal: Vec2;
  obstacleId: string;
  material: TerrainMaterial | 'boundary';
}

export interface SweepResult {
  position: Vec2;
  attemptedDistanceM: number;
  actualDistanceM: number;
  remainingDistanceM: number;
  collision: TerrainHit | null;
}

export type BlockingMode = 'movement' | 'projectile' | 'areaEffect';

export function emptyTerrain(arenaRadiusM: number): Terrain {
  return {
    boundary: { type: 'circle', center: { x: 0, y: 0 }, radiusM: arenaRadiusM },
    obstacles: [],
    zones: [],
  };
}

export function distance(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function pointInShape(point: Vec2, shape: Shape): boolean {
  if (shape.type === 'circle') {
    return Math.hypot(point.x - shape.center.x, point.y - shape.center.y) <= shape.radiusM + 1e-9;
  }
  return point.x >= shape.minX && point.x <= shape.maxX && point.y >= shape.minY && point.y <= shape.maxY;
}

export function zonesAt(terrain: Terrain, point: Vec2): TerrainZone[] {
  return terrain.zones.filter((zone) => pointInShape(point, zone.shape));
}

/** Signed contribution to Φ: hazards are dangerous, safe zones reduce danger. */
export function zoneDanger(terrain: Terrain, point: Vec2): number {
  let value = 0;
  for (const zone of zonesAt(terrain, point)) {
    if (zone.kind === 'hazard' || zone.kind === 'cliff') value += zone.intensity;
    else if (zone.kind === 'safe') value -= zone.intensity * 10;
  }
  return value;
}

export function lineOfEffect(terrain: Terrain, from: Vec2, to: Vec2, mode: Exclude<BlockingMode, 'movement'>): boolean {
  return firstTerrainHit(terrain, from, to, mode, 0, false) === null;
}

/** Sweep a circular actor through the arena and stop at the first collision. */
export function sweepMovement(terrain: Terrain, from: Vec2, delta: Vec2, actorRadiusM: number): SweepResult {
  const attemptedDistanceM = Math.hypot(delta.x, delta.y);
  if (attemptedDistanceM < 1e-12) {
    return { position: { ...from }, attemptedDistanceM: 0, actualDistanceM: 0, remainingDistanceM: 0, collision: null };
  }
  const to = { x: from.x + delta.x, y: from.y + delta.y };
  const hit = firstTerrainHit(terrain, from, to, 'movement', actorRadiusM, true);
  if (!hit) {
    return { position: to, attemptedDistanceM, actualDistanceM: attemptedDistanceM, remainingDistanceM: 0, collision: null };
  }
  const epsilonT = Math.min(1e-4 / attemptedDistanceM, hit.t);
  const t = Math.max(0, hit.t - epsilonT);
  const position = { x: from.x + delta.x * t, y: from.y + delta.y * t };
  const actualDistanceM = attemptedDistanceM * t;
  return {
    position,
    attemptedDistanceM,
    actualDistanceM,
    remainingDistanceM: Math.max(0, attemptedDistanceM - actualDistanceM),
    collision: hit,
  };
}

function firstTerrainHit(
  terrain: Terrain,
  from: Vec2,
  to: Vec2,
  mode: BlockingMode,
  actorRadiusM: number,
  includeBoundary: boolean,
): TerrainHit | null {
  let best: TerrainHit | null = null;
  const consider = (hit: TerrainHit | null): void => {
    if (hit && hit.t >= 0 && hit.t <= 1 && (!best || hit.t < best.t)) best = hit;
  };

  if (includeBoundary) consider(boundaryExit(terrain, from, to, actorRadiusM));
  for (const obstacle of terrain.obstacles) {
    const blocks =
      mode === 'movement'
        ? obstacle.blocksMovement
        : mode === 'projectile'
          ? obstacle.blocksProjectile
          : obstacle.blocksAreaEffect;
    if (!blocks) continue;
    const t = segmentShapeEntry(from, to, obstacle.shape, actorRadiusM);
    if (t === null) continue;
    const point = lerp(from, to, t);
    consider({ t, point, normal: shapeNormal(point, obstacle.shape), obstacleId: obstacle.id, material: obstacle.material });
  }
  return best;
}

function boundaryExit(terrain: Terrain, from: Vec2, to: Vec2, actorRadiusM: number): TerrainHit | null {
  const b = terrain.boundary;
  const radius = Math.max(0, b.radiusM - actorRadiusM);
  const fx = from.x - b.center.x;
  const fy = from.y - b.center.y;
  const tx = to.x - b.center.x;
  const ty = to.y - b.center.y;
  const fromR2 = fx * fx + fy * fy;
  const toR2 = tx * tx + ty * ty;
  // Spawn recovery: an actor already outside the inset collision boundary may
  // move inward, but cannot move farther out.
  if (fromR2 > radius * radius + 1e-9) {
    if (toR2 < fromR2) return null;
    const m = Math.sqrt(fromR2) || 1;
    return {
      t: 0,
      point: { ...from },
      normal: { x: -fx / m, y: -fy / m },
      obstacleId: '__arena_boundary__',
      material: 'boundary',
    };
  }
  if (toR2 <= radius * radius + 1e-9) return null;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const a = dx * dx + dy * dy;
  const bb = 2 * (fx * dx + fy * dy);
  const c = fx * fx + fy * fy - radius * radius;
  const disc = Math.max(0, bb * bb - 4 * a * c);
  const roots = [(-bb - Math.sqrt(disc)) / (2 * a), (-bb + Math.sqrt(disc)) / (2 * a)].filter((t) => t >= 0 && t <= 1);
  const t = roots.length ? Math.min(...roots) : 0;
  const point = lerp(from, to, t);
  const m = Math.hypot(point.x - b.center.x, point.y - b.center.y) || 1;
  return {
    t,
    point,
    normal: { x: -(point.x - b.center.x) / m, y: -(point.y - b.center.y) / m },
    obstacleId: '__arena_boundary__',
    material: 'boundary',
  };
}

function segmentShapeEntry(from: Vec2, to: Vec2, shape: Shape, expand: number): number | null {
  if (shape.type === 'circle') {
    const radius = shape.radiusM + expand;
    const fx = from.x - shape.center.x;
    const fy = from.y - shape.center.y;
    if (fx * fx + fy * fy <= radius * radius) return 0;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const a = dx * dx + dy * dy;
    const b = 2 * (fx * dx + fy * dy);
    const c = fx * fx + fy * fy - radius * radius;
    const disc = b * b - 4 * a * c;
    if (disc < 0 || a < 1e-12) return null;
    const t = (-b - Math.sqrt(disc)) / (2 * a);
    return t >= 0 && t <= 1 ? t : null;
  }

  const minX = shape.minX - expand;
  const maxX = shape.maxX + expand;
  const minY = shape.minY - expand;
  const maxY = shape.maxY + expand;
  if (from.x >= minX && from.x <= maxX && from.y >= minY && from.y <= maxY) return 0;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  let lo = 0;
  let hi = 1;
  for (const [origin, delta, min, max] of [
    [from.x, dx, minX, maxX],
    [from.y, dy, minY, maxY],
  ] as const) {
    if (Math.abs(delta) < 1e-12) {
      if (origin < min || origin > max) return null;
    } else {
      const a = (min - origin) / delta;
      const b = (max - origin) / delta;
      lo = Math.max(lo, Math.min(a, b));
      hi = Math.min(hi, Math.max(a, b));
      if (lo > hi) return null;
    }
  }
  return lo >= 0 && lo <= 1 ? lo : null;
}

function shapeNormal(point: Vec2, shape: Shape): Vec2 {
  if (shape.type === 'circle') {
    const dx = point.x - shape.center.x;
    const dy = point.y - shape.center.y;
    const m = Math.hypot(dx, dy) || 1;
    return { x: dx / m, y: dy / m };
  }
  const distances = [
    { d: Math.abs(point.x - shape.minX), n: { x: -1, y: 0 } },
    { d: Math.abs(point.x - shape.maxX), n: { x: 1, y: 0 } },
    { d: Math.abs(point.y - shape.minY), n: { x: 0, y: -1 } },
    { d: Math.abs(point.y - shape.maxY), n: { x: 0, y: 1 } },
  ];
  return distances.sort((a, b) => a.d - b.d)[0]!.n;
}

function lerp(a: Vec2, b: Vec2, t: number): Vec2 {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}
